import React, { useState, useEffect, useMemo } from 'react';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Box from '@mui/material/Box';
import Loading from '../Loading';
import Button from '@mui/material/Button';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Paper from '@mui/material/Paper';
import Notifications from '../../services/Notifications';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Typography from '@mui/material/Typography';
import MUIModal from '../shared/MUIModal';
import MUIAlertModal from '../shared/MUIAlertModal';


export const OptionalOrderTypeAdministration = () => {
    const [rows, setRows] = useState([]);
    const [fetching, setFetching] = useState(false);
    const localized = useStorefrontAdministrationConfig().labels;
    const storeData = useStorefrontSettings();
    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const theme = StorefrontAdminTheme;
    const responseMessages = {
        "DuplicateKeyViolation": localized.OptionalOrderTypeDuplicateCodeViolation || 'Duplicate Data Violation',
        "GenericFailure": localized.OptionalOrderTypeGenericFailure || 'Generic Failure',
        "DeleteSuccess": localized.OptionalOrderTypeDeleteSuccess || 'Delete Success',
        "UpdateSuccess": localized.OptionalOrderTypeUpdateSuccess || 'Update Success',
        "AddSuccess": localized.OptionalOrderTypeAddSuccess || 'Add Success'
    };

    useEffect(() => {
        getOptionalOrderTypes();
    }, []);

    const customToolbar = () => {
        return (
            <GridToolbarContainer>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
                {renderAddButton()}
            </GridToolbarContainer>
        );
    }

    const getOptionalOrderTypes = () => {
        setFetching(true);
        Storefront.getOptionalOrderTypes(storeData.storefrontNumber).then(loaded);
    }

    const updateOptionalOrderType = async (form, type) => {
        var request = {
            id: form.Id ?? 0,
            companyCode: storeData.storefrontNumber,
            customerId: form.customerId ?? null,
            orderType: form.orderType ?? null,
            storefrontId: storeData.storefront.id,
            operationType: type
        };
        return await Storefront.updateOptionalOrderType(request).then((res) => {
            if (res) {
                const notificationType = res.response.endsWith("Success") ? "message" : "error";
                const message = responseMessages[res.response];

                if (message) {
                    Notifications[notificationType](message);
                }
                else {
                    Notifications.error(localized.OptionalOrderTypeFailUpdateNotification || 'Fail Update');
                }
                getOptionalOrderTypes();
            }
        });
    }

    const loaded = (data) => {
        if (data) {
            mapRows(data);
        }
        setFetching(false);
    }

    const mapRows = (data) => {
        const rows = [];
        (data.optionalOrderType || []).forEach((psc, index) => {
            rows.push({ id: index, optionalId: psc.id, customerId: psc.customerId, orderType: psc.orderType });
        });
        setRows(rows);
    };


    const handleClickOpenUpdate = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setOpenUpdate(true);
    };

    const handleCloseUpdate = () => {
        setOpenUpdate(false);
    };
    const handleClickOpenDelete = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    const handleClickOpenAdd = () => {
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const renderDeleteButton = (row) => (
        <MUIAlertModal
            open={openDelete}
            onClose={handleCloseDelete}
            title={localized.AreYouSure}
            message={
                <>
                    <h3>{localized.AreYouSureDeleteOptionalOrderType || 'Are you sure you want to delete this Optional Order Type for Customer?'}</h3>
                    <DialogContentText>
                        <b>{row.customerId}</b>
                    </DialogContentText>
                </>
            }
            confirmLabel={localized.StorefrontAdminDelete}
            cancelLabel={localized.StorefrontAdminCancel}
            onConfirm={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.optionalId);
                const formJson = Object.fromEntries(formData.entries());
                updateOptionalOrderType(formJson, "delete");
                handleCloseDelete();
            }}
            confirmButtonProps={{ color: "error" }}
            cancelButtonProps={{ color: "update" }}
        />
    );

    const renderUpdateButton = (row) => (
        <MUIModal
            open={openUpdate}
            onClose={handleCloseUpdate}
            title={localized.UpdateOptionalOrderType || 'Update Optional Order Type'}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.optionalId);
                formData.append("customerId", row.customerId);
                formData.append("OrderType", row.orderType);
                const formJson = Object.fromEntries(formData.entries());
                updateOptionalOrderType(formJson, "update");
                handleCloseUpdate();
            }}
            submitLabel={localized.StorefrontAdminUpdate}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "update" }}
            cancelButtonProps={{ color: "update" }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContentText>
                <h3>{localized.ValuesEligibleForUpdateBelow}</h3>
            </DialogContentText>
           
            <TextField
                defaultValue={row.customerId}
                margin="dense"
                id="customerId"
                name="customerId"
                label={localized.Customer || "Customer"}
                type="text"
                fullWidth
                variant="standard"
                disabled
            />
            <TextField
                defaultValue={row.orderType}
                inputProps={{ maxLength: 500 }}                
                margin="dense"
                id="orderType"
                name="orderType"
                label={localized.OrderType || "Order Type"}
                type="text"
                fullWidth
                variant="standard"
                required
            />
        </MUIModal>
    );

    const renderAddButton = () => (
        <strong>
            <Button
                variant="contained"
                color="success"
                style={{ marginLeft: 16 }}
                onClick={handleClickOpenAdd}
            >
                {localized.StorefrontAdminAdd}
            </Button>
        </strong>
    );


    const renderAddModal = () => (
        <MUIModal
            open={openAdd}
            onClose={handleCloseAdd}
            title={localized.AddOptionalOrderType || 'Add Optional Order Type'}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                updateOptionalOrderType(formJson, "add");
                handleCloseAdd();
            }}
            submitLabel={localized.StorefrontAdminAdd}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "success" }}
            cancelButtonProps={{ color: "error" }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContentText>
                <h3>{localized.FillRequiredFieldsBelow}</h3>
            </DialogContentText>
            <TextField
                inputProps={{ maxLength: 8 }}
                margin="dense"
                id="customerId"
                name="customerId"
                label={localized.CustomerNumber || "Customer"}
                type="number"
                fullWidth
                variant="standard"
                required
            />
            <TextField
                inputProps={{ maxLength: 5 }}
                margin="dense"
                id="orderType"
                name="orderType"
                label={localized.OrderType || "Order Type"}
                type="text"
                fullWidth
                variant="standard"
                required
            />
        </MUIModal>
    );


    const columns = [
        { field: 'optionalId', headerName: localized.StorefrontAdminid || 'Id' },
        { field: 'customerId', headerName: localized.CustomerNumber || 'Customer', flex: 1, headerAlign: 'center', align: 'center' },
        { field: 'orderType', headerName: localized.StorefrontAdminOrderType || 'OrderType', flex: 1, headerAlign: 'center', align: 'center' },
        {
            field: 'updateButton',
            headerName: localized.StorefrontAdminUpdate,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.StorefrontAdminUpdate}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true

        },
        {
            field: 'deleteButton',
            headerName: localized.StorefrontAdminDelete,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'columnTopper',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="error"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenDelete(e, params.row)}
                        >
                            {localized.StorefrontAdminDelete}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true
        }
    ];

    const productItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid
                    rows={rows}
                    columns={columns}
                    insetToolbar={customToolbar}
                    labels={localized}
                    columnVisibilityModel={{ optionalId: false }}
                    getRowId={(row) => row.optionalId}
                />

            </Paper>
        );
    }, [rows, localized]);

    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1">{localized.OptionalOrderTypeAdministrationHeader || "Optional Order Type"}</Typography>
                        {productItems}
                        {openUpdate && renderUpdateButton(selectedRow)}
                        {openDelete && renderDeleteButton(selectedRow)}
                        {openAdd && renderAddModal()}
                    </ThemeProvider>
                </>
            }
        </>
    );
}

export default OptionalOrderTypeAdministration;

